import { InputLabel } from '@mui/material';
import { type ChangeEvent } from 'react';
import { KeywordMultiSelectDropdown } from '#components/Filters/KeywordMultiSelectDropdown/index.tsx';
import { areOptions } from '#components/Filters/helpers/otherHelpers.ts';
import { type FiltersConfig } from '#types.ts';
import { InputLabelWithCheckbox } from '../../Form/InputLabelWithCheckbox/index.tsx';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { OuterQuery as CompanyMultiSelectDropdown } from '../CompanyMultiSelectDropdown/index.ts';
import { FirstAiredRangeSlider } from '../FirstAiredRangeSlider/index.tsx';
import { GenreMultiSelectDropdown } from '../GenreMultiSelectDropdown/index.tsx';
import { RuntimeRangeSlider } from '../RuntimeRangeSlider/index.tsx';
import { TvStatusMultiSelectDropdown } from '../TvStatusMultiSelectDropdown/index.tsx';
import { TvTypeMultiSelectDropdown } from '../TvTypeMultiSelectDropdown/index.tsx';
import { VoteAverageRangeSlider } from '../VoteAverageRangeSlider/index.tsx';
import { type Option, type TvFormikValues, type UseFormik } from '../types.ts';

export type TvFiltersProps = {
  config: FiltersConfig;
  filterOptions: Record<string, Mark[] | Option[]>;
  formik: UseFormik<TvFormikValues>;
  onRangeSliderToggle: (
    event: ChangeEvent<HTMLInputElement>,
    name: keyof TvFormikValues,
    checked: boolean,
    defaultValue: number[],
  ) => void;
};

export const TvFilters = ({ config, filterOptions, formik, onRangeSliderToggle }: TvFiltersProps) => {
  const { rangeSliders } = config;

  return (
    <>
      <InputLabel
        id="type-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        In category
      </InputLabel>
      <TvTypeMultiSelectDropdown
        ariaLabelledBy="type-filter-label"
        name="type"
        setFieldValue={formik.setFieldValue}
        value={formik.values.type}
      />
      <InputLabel
        id="status-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With status
      </InputLabel>
      <TvStatusMultiSelectDropdown
        ariaLabelledBy="status-filter-label"
        name="status"
        setFieldValue={formik.setFieldValue}
        value={formik.values.status}
      />
      <InputLabelWithCheckbox
        checked={!rangeSliders?.firstAired?.disabled || false}
        id="first-aired-years-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'firstAired', checked, [new Date().getFullYear() - 10, new Date().getFullYear()]);
        }}
      >
        First aired
      </InputLabelWithCheckbox>
      <FirstAiredRangeSlider
        ariaLabelledBy="first-aired-years-filter-label"
        disabled={rangeSliders?.firstAired?.disabled ?? false}
        name="firstAired"
        onChange={formik.handleChange}
        value={formik.values.firstAired}
      />
      <InputLabelWithCheckbox
        checked={!rangeSliders?.voteAverages?.disabled || false}
        id="vote-averages-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'voteAverages', checked, [0, 10]);
        }}
      >
        Vote average
      </InputLabelWithCheckbox>
      <VoteAverageRangeSlider
        ariaLabelledBy="vote-averages-filter-label"
        disabled={rangeSliders?.voteAverages?.disabled ?? false}
        name="voteAverages"
        onChange={formik.handleChange}
        value={formik.values.voteAverages}
      />
      <InputLabelWithCheckbox
        checked={!rangeSliders?.runtimes?.disabled || false}
        id="runtimes-filter-label"
        onChange={(e, checked) => {
          onRangeSliderToggle(e, 'runtimes', checked, [90, 150]);
        }}
      >
        Runtime (hrs)
      </InputLabelWithCheckbox>
      <RuntimeRangeSlider
        ariaLabelledBy="runtimes-filter-label"
        disabled={rangeSliders?.runtimes?.disabled ?? false}
        name="runtimes"
        onChange={formik.handleChange}
        value={formik.values.runtimes}
      />
      <InputLabel
        id="companies-filter-label"
        sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
      >
        With company
      </InputLabel>
      <CompanyMultiSelectDropdown
        ariaLabelledBy="companies-filter-label"
        name="companies"
        setFieldValue={formik.setFieldValue}
        value={formik.values.companies}
      />
      {areOptions(filterOptions.genres) ? (
        <>
          <InputLabel
            id="with-genres-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            With genre
          </InputLabel>
          <GenreMultiSelectDropdown
            ariaLabelledBy="with-genres-filter-label"
            name="withGenres"
            options={filterOptions.genres}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withGenres}
          />
          <InputLabel
            id="without-genres-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            Without genre
          </InputLabel>
          <GenreMultiSelectDropdown
            ariaLabelledBy="without-genres-filter-label"
            name="withoutGenres"
            options={filterOptions.genres}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withoutGenres}
          />
        </>
      ) : undefined}
      {areOptions(filterOptions.keywords) ? (
        <>
          <InputLabel
            id="with-keywords-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            With keyword
          </InputLabel>
          <KeywordMultiSelectDropdown
            ariaLabelledBy="with-keywords-filter-label"
            name="withKeywords"
            options={filterOptions.keywords}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withKeywords}
          />
          <InputLabel
            id="without-keywords-filter-label"
            sx={{ color: 'inherit', marginBottom: theme => theme.spacing(1), marginTop: theme => theme.spacing(2) }}
          >
            Without keyword
          </InputLabel>
          <KeywordMultiSelectDropdown
            ariaLabelledBy="without-keywords-filter-label"
            name="withoutKeywords"
            options={filterOptions.keywords}
            setFieldValue={formik.setFieldValue}
            value={formik.values.withoutKeywords}
          />
        </>
      ) : undefined}
      <InputLabelWithCheckbox
        checked={formik.values.screenedTheatrically}
        id="screened-theatrically-filter-label"
        inputProps={{
          name: 'screenedTheatrically',
        }}
        onChange={formik.handleChange}
      >
        Screened theatrically
      </InputLabelWithCheckbox>
    </>
  );
};
